import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { ToastAction } from "@/components/Toast/Toast"
import { useToast } from "@/components/Toast"
import {
  useCommsContact,
  useCommsOrgs,
  useSetContactRelevance,
  usePatchContact,
  formatAge,
  DOCS_CONTACTS_BASE_URL,
  Commitment,
  FollowupStatus,
} from "@/hooks/useComms"
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline"

function CommitmentList({
  title,
  items,
  empty,
}: {
  title: string
  items: Commitment[]
  empty: string
}) {
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-primary">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((c, i) => (
            <li key={`${c.message_public_id}-${i}`} className="text-sm text-primary">
              <span>{c.text}</span>
              <span className="ml-2 text-xs text-muted-foreground">{formatAge(c.sent_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function CommsContactDetail() {
  const { publicId } = useParams<{ publicId: string }>()
  const { toast } = useToast()
  const { data, isLoading, error } = useCommsContact(publicId || "")
  const setRelevance = useSetContactRelevance()
  const patchContact = usePatchContact()
  // The org list backs the picker only — load it lazily when the picker opens, so
  // a plain contact view costs zero org queries. The domain SUGGESTION comes from
  // the server (data.suggested_org), independent of this list.
  const [pickerOpen, setPickerOpen] = useState(false)
  const { data: orgsData, isLoading: orgsLoading } = useCommsOrgs({
    limit: 200,
    enabled: pickerOpen,
  })

  const [confirmNotRelevant, setConfirmNotRelevant] = useState(false)

  // Seed the note + follow-up forms from the contact read. Keyed off the contact's
  // public_id (not a one-shot flag) so navigating directly between two contact
  // URLs on the same mounted component re-seeds for the NEW contact instead of
  // leaving the previous one's note/disposition in the form (which Save would then
  // write to the wrong contact). A same-contact refetch (e.g. after a save) does
  // NOT re-seed, so an in-flight edit isn't clobbered.
  const [note, setNote] = useState("")
  const [followupStatus, setFollowupStatus] = useState<FollowupStatus>("active")
  const [followupReason, setFollowupReason] = useState("")
  const [seededFor, setSeededFor] = useState<string | null>(null)

  useEffect(() => {
    if (data && seededFor !== data.contact.public_id) {
      setNote(data.contact.notes ?? "")
      setFollowupStatus((data.contact.followup_status as FollowupStatus | null) ?? "active")
      setFollowupReason(data.contact.followup_reason ?? "")
      setSeededFor(data.contact.public_id)
    }
  }, [data, seededFor])

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 rounded bg-muted" />
          <div className="h-40 rounded bg-muted" />
        </div>
      </Layout>
    )
  }

  const { contact, emails, threads, commitments, suggested_org: suggestedOrg } = data
  const contactLabel = contact.display_name || contact.primary_email

  const markNotRelevant = () => {
    setRelevance.mutate(
      { publicId: contact.public_id, relevant: false },
      {
        onSuccess: () => {
          toast({
            title: "Marked not relevant",
            description: `${contactLabel} and all their mail are now hidden.`,
            action: (
              <ToastAction
                altText="Restore this contact"
                onClick={() =>
                  setRelevance.mutate({ publicId: contact.public_id, relevant: true })
                }
              >
                Undo
              </ToastAction>
            ),
          })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark not relevant. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const restore = () => {
    setRelevance.mutate(
      { publicId: contact.public_id, relevant: true },
      {
        onSuccess: () =>
          toast({ title: "Restored", description: `${contactLabel} is back in the active view.` }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to restore. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const saveNote = () => {
    patchContact.mutate(
      { publicId: contact.public_id, notes: note.trim() === "" ? null : note },
      {
        onSuccess: () => toast({ title: "Note saved" }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to save the note. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const saveFollowup = (status: FollowupStatus) => {
    patchContact.mutate(
      {
        publicId: contact.public_id,
        followup_status: status,
        followup_reason: followupReason.trim() === "" ? null : followupReason,
      },
      {
        onSuccess: () => {
          setFollowupStatus(status)
          toast({
            title:
              status === "no_action"
                ? "Follow-up dismissed"
                : status === "snoozed"
                  ? "Snoozed"
                  : "Follow-up updated",
            description:
              status === "active"
                ? "This contact is back in the follow-up digests."
                : "Digests will stop nagging about this contact. They stay a real contact — relevance is unchanged.",
          })
        },
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to update follow-up. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const isQuarantined = contact.is_relevant === false

  const assignOrg = (orgPublicId: string | null) => {
    patchContact.mutate(
      { publicId: contact.public_id, org_public_id: orgPublicId },
      {
        onSuccess: () => toast({ title: orgPublicId ? "Assigned to org" : "Removed from org" }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to update the org. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  // `suggestedOrg` is server-resolved (data.suggested_org). The list below backs
  // the manual picker, loaded only once it's opened.
  const orgs = orgsData?.orgs ?? []
  const emailDomain = contact.primary_email.split("@")[1]?.toLowerCase() ?? null

  return (
    <Layout>
      <div className="space-y-6">
        <Link
          to="/comms/contacts"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" /> All contacts
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {contact.display_name || contact.primary_email}
            </h1>
            <p className="text-muted-foreground">
              {contact.org || "Unknown org"} · last contacted {formatAge(contact.last_contacted_at)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {contact.doc_slug && (
              <a
                href={`${DOCS_CONTACTS_BASE_URL}/${contact.doc_slug}.md`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Discovery doc <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            )}
            {isQuarantined ? (
              <Button variant="outline" size="sm" onClick={restore}>
                Restore
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setConfirmNotRelevant(true)}
              >
                Not relevant
              </Button>
            )}
          </div>
        </div>

        {isQuarantined && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            This contact is marked <span className="font-medium">not relevant</span> — hidden from
            comms views and queued for purge. Restore to bring them back.
          </div>
        )}

        {/* Addresses */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-semibold text-primary">Addresses</h2>
          <ul className="space-y-1">
            {emails.map((e) => (
              <li key={e.email} className="flex items-center gap-2 text-sm">
                <EnvelopeIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-primary">{e.email}</span>
                {e.is_primary && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    primary
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>

        {/* Organization (A3) — assign/change the org; domain-suggested when unassigned */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-semibold text-primary">Organization</h2>
          {contact.org_ref ? (
            <p className="mb-3 text-sm text-primary">
              Member of{" "}
              <Link
                to={`/comms/orgs/${contact.org_ref.public_id}`}
                className="text-blue-600 hover:underline"
              >
                {contact.org_ref.name}
              </Link>
            </p>
          ) : (
            <p className="mb-3 text-sm text-muted-foreground">
              Not assigned to an org{contact.org ? ` (free-text: ${contact.org})` : ""}.
            </p>
          )}
          {suggestedOrg && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <span>
                Suggested from <span className="font-medium">{emailDomain}</span>: {suggestedOrg.name}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={patchContact.isPending}
                onClick={() => assignOrg(suggestedOrg.public_id)}
              >
                Assign
              </Button>
            </div>
          )}
          {pickerOpen ? (
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={contact.org_ref?.public_id ?? ""}
                onChange={(e) => assignOrg(e.target.value || null)}
                disabled={patchContact.isPending || orgsLoading}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">{orgsLoading ? "Loading…" : "— None —"}</option>
                {orgs.map((o) => (
                  <option key={o.public_id} value={o.public_id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <Link to="/comms/orgs" className="text-xs text-blue-600 hover:underline">
                Manage orgs
              </Link>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                {contact.org_ref ? "Change org" : "Assign org"}
              </Button>
              <Link to="/comms/orgs" className="text-xs text-blue-600 hover:underline">
                Manage orgs
              </Link>
            </div>
          )}
        </section>

        {/* Follow-up disposition (separate from relevance) */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <div className="mb-1 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-primary">Follow-up</h2>
            <span className="text-xs text-muted-foreground">Separate from relevance</span>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Stop the going-cold / owed-a-reply digests from nagging about a real contact you don’t
            need to chase — without quarantining them. (e.g. covered via someone else in their org.)
          </p>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Reason (optional)
          </label>
          <input
            type="text"
            value={followupReason}
            onChange={(e) => setFollowupReason(e.target.value)}
            placeholder="e.g. covered via Quincy / Texas Syndicate"
            className="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={patchContact.isPending}
              onClick={() => saveFollowup("no_action")}
            >
              No further follow-up
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={patchContact.isPending}
              onClick={() => saveFollowup("snoozed")}
            >
              Snooze
            </Button>
            {followupStatus !== "active" && (
              <Button
                variant="ghost"
                size="sm"
                disabled={patchContact.isPending}
                onClick={() => saveFollowup("active")}
              >
                Reactivate
              </Button>
            )}
          </div>
        </section>

        {/* Contact note */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-semibold text-primary">Note</h2>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Add context about this contact…"
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={patchContact.isPending} onClick={saveNote}>
              Save note
            </Button>
          </div>
        </section>

        {/* Open commitments */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-primary">Open commitments</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <CommitmentList
              title="They asked"
              items={commitments.they_asked}
              empty="Nothing recorded."
            />
            <CommitmentList
              title="You promised"
              items={commitments.i_promised}
              empty="Nothing recorded."
            />
            <CommitmentList
              title="Suggested follow-up"
              items={commitments.suggested_followup}
              empty="None suggested."
            />
          </div>
        </section>

        {/* Threads */}
        <section>
          <h2 className="mb-2 text-lg font-semibold text-primary">
            Threads <span className="text-sm font-normal text-muted-foreground">({threads.length})</span>
          </h2>
          {threads.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">No threads captured.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {threads.map((t) => (
                <li key={t.public_id}>
                  <Link
                    to={`/comms/threads/${t.public_id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-primary">
                        {t.subject || "(no subject)"}
                      </p>
                      <p className="text-xs text-muted-foreground">{t.message_count} messages</p>
                    </div>
                    <div className="ml-4 flex shrink-0 items-center gap-2">
                      {t.resolved_at ? (
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          resolved
                        </span>
                      ) : (
                        t.still_owed && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            owed
                          </span>
                        )
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatAge(t.last_inbound_at || t.last_outbound_at)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={confirmNotRelevant}
        onOpenChange={setConfirmNotRelevant}
        title="Mark contact not relevant?"
        description={`${contactLabel} and all their mail (existing and future) will be hidden from every comms view and queued for purge in 30 days. You can undo this or restore them from the “Not relevant” bucket.`}
        confirmLabel="Not relevant"
        confirmVariant="destructive"
        onConfirm={markNotRelevant}
      />
    </Layout>
  )
}
